import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { createUseStyles, Select } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { getEntityValue } from 'lib/services/valueLists';
import {
    applyLineSmartFieldOverridesToInputParams,
    getLineSmartFieldOverrides,
    resolveCellIsEnriched,
    toCellValidationError,
} from './applyLineSmartFieldOverrides';

import styles from './styles';

const useStyles = createUseStyles(styles, 'SelectCell');

const hasSelectObjectWithId = (raw) =>
    raw != null &&
    typeof raw === 'object' &&
    Object.prototype.hasOwnProperty.call(raw, 'value') &&
    raw.value !== undefined &&
    raw.value !== null;

const toSelectOption = (raw) => {
    if (hasSelectObjectWithId(raw)) {
        const label = raw.description ?? raw.label ?? '';
        return { label, value: String(raw.value) };
    }
    if (raw === null || raw === undefined || raw === '') return undefined;
    return { label: String(raw), value: String(raw) };
};

const isEmptyValue = (raw) =>
    raw === null || raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0);

const toStructuredValue = (raw, isMulti) => {
    if (isMulti && Array.isArray(raw) && raw.every(hasSelectObjectWithId)) {
        return raw.map(toSelectOption);
    }
    if (!isMulti && hasSelectObjectWithId(raw)) {
        return toSelectOption(raw);
    }
    return undefined;
};

const toPrimitiveIds = (raw, isMulti) => {
    if (!isMulti) return [raw];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.includes(';')) {
        return raw.split(';').filter(Boolean);
    }
    return [raw];
};

const SelectCell = memo((params) => {
    const { t } = useWeb5Context();
    const [value, setValue] = useState(() => toSelectOption(params.value));
    const [error, setError] = useState('');
    const resolvedPrimitiveRef = useRef(null);
    const classes = useStyles();

    const field = params.colDef.field;
    const lineOverrides = getLineSmartFieldOverrides(params.data, field);
    const resolvedInputParams = applyLineSmartFieldOverridesToInputParams(
        params.inputParams,
        lineOverrides,
    );
    const {
        onChange,
        isMulti,
        list,
        isEnriched: isEnrichedParam,
        ...inputParams
    } = resolvedInputParams || {};
    const isReadOnly = !!(
        lineOverrides?.readOnly ||
        lineOverrides?.hidden ||
        inputParams.isReadOnly ||
        inputParams.readOnly
    );
    const isEnriched = resolveCellIsEnriched(
        { ...inputParams, isEnriched: isEnrichedParam },
        params.data,
        field,
        isReadOnly,
    );

    const handleOnChange = useCallback(
        (newValue) => {
            if (isReadOnly) return;
            const id = params.data.id || params.data.Id || params.data.ID || '';
            onChange({ rowIndex: params.node.rowIndex, id, field, value: newValue });
            setValue(newValue);
        },
        [isReadOnly, onChange, field, params],
    );

    useEffect(() => {
        const rowIndex = params.node.rowIndex;
        const rowError = inputParams?.errors?.rows?.[rowIndex]?.error;
        setError(toCellValidationError(rowError));
    }, [inputParams.errors, params]);

    useEffect(() => {
        const raw = isEmptyValue(params?.value) ? inputParams.defaultValue : params?.value;
        const structuredValue = toStructuredValue(raw, isMulti);
        if (structuredValue) {
            const structuredKey = isMulti
                ? structuredValue.map((option) => option.value).join('|')
                : structuredValue.value;
            resolvedPrimitiveRef.current = structuredKey;
            setValue((prev) =>
                JSON.stringify(prev) === JSON.stringify(structuredValue) ? prev : structuredValue,
            );
            return undefined;
        }
        if (isEmptyValue(raw)) {
            resolvedPrimitiveRef.current = null;
            setValue(undefined);
            return undefined;
        }

        const ids = toPrimitiveIds(raw, isMulti);
        const rawKey = ids.map(String).join('|');
        if (resolvedPrimitiveRef.current === rawKey) {
            return undefined;
        }

        let cancelled = false;
        getEntityValue(list, ids).then((result) => {
            if (cancelled) return;
            if (!result?.data) return;
            const finalValue = isMulti
                ? ids
                      .map((id) => {
                          const row = result.data[id] ?? result.data[String(id)];
                          if (!row) return null;
                          return {
                              label: row.description ?? row.label ?? '',
                              value: String(row.id ?? id),
                          };
                      })
                      .filter(Boolean)
                : (() => {
                      const id = ids[0];
                      const row = result.data[id] ?? result.data[String(id)];
                      if (row == null) return null;
                      return {
                          label: row.description ?? row.label ?? '',
                          value: String(row.id ?? id),
                      };
                  })();
            if (finalValue != null) {
                resolvedPrimitiveRef.current = rawKey;
                setValue(isMulti && finalValue.length === 0 ? undefined : finalValue);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [inputParams.defaultValue, isMulti, list, params?.value]);

    if (!params.inputParams) return null;

    const placeholder = isReadOnly
        ? t('label_advanced_filter_option_empty')
        : (inputParams.placeholder ??
          t(isMulti || inputParams.isFuzzy ? 'placeholder_search' : 'label_selectone'));

    return (
        <div className={['w5-cell', classes.editableCell].join(' ')}>
            <Select
                value={value}
                onChange={handleOnChange}
                list={list}
                isMulti={isMulti}
                {...inputParams}
                isReadOnly={isReadOnly}
                readOnly={isReadOnly}
                placeholder={placeholder}
                error={error}
                isEnriched={isEnriched}
                isFullWidth
            />
        </div>
    );
});

export default SelectCell;
